import PropTypes from "prop-types";
import ReactQuill from "react-quill";
import { useRef, useCallback } from "react";
import axiosInstance from "src/utils/axios";
import "react-quill/dist/quill.snow.css";

// @mui
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";

// i18n
import { useTranslation } from "react-i18next";

// Toolbar
import EditorToolbar, {
  formats,
  redoChange,
  undoChange,
} from "./EditorToolbar";

// ----------------------------------------------------------------------

const RootStyle = styled(Box)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  border: `solid 1px ${theme.palette.grey[500_32]}`,

  "& .ql-container.ql-snow": {
    borderColor: "transparent",
    ...theme.typography.body1,
    fontFamily: theme.typography.fontFamily,
  },

  "& .ql-editor": {
    minHeight: 200,

    "&.ql-blank::before": {
      fontStyle: "normal",
      color: theme.palette.text.disabled,
    },

    "& pre.ql-syntax": {
      ...theme.typography.body2,
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.grey[900],
    },

    /* -----------------------------
       VIDEO EMBED RESIZING
    ------------------------------ */
    "& .ql-video": {
      width: "100%",
      maxWidth: "100%",
      aspectRatio: "16 / 9",
      height: "auto",
      borderRadius: theme.shape.borderRadius,
    },

    /* Mobile friendly */
    [theme.breakpoints.down("sm")]: {
      "& .ql-video": {
        aspectRatio: "16 / 9",
      },
    },
  },
}));

// ----------------------------------------------------------------------

Editor.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.node,
  simple: PropTypes.bool,
  sx: PropTypes.object,
  placeholder: PropTypes.string,
};

// ----------------------------------------------------------------------

export default function Editor({
  id = "cloud-quill",
  value,
  onChange,
  error = false,
  helperText,
  simple = true,
  sx,
  placeholder,
  ...other
}) {
  const { t } = useTranslation();
  const quillRef = useRef(null);

  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      if (input !== null && input.files !== null) {
        const file = input.files[0];
        const formData = new FormData();
        formData.append("media", file);

        try {
          const { data } = await axiosInstance.post(
            "/api/admin/media",
            formData,
          );
          const url = data?.url;
          console.log(data);

          const quill = quillRef.current.getEditor();
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, "image", url);
          quill.setSelection(range.index + 1);
        } catch (err) {
          console.error("Image upload failed", err);
        }
      }
    };
  }, []);

  const modules = {
    toolbar: {
      container: `#${id}`,
      handlers: {
        undo: undoChange,
        redo: redoChange,
        image: imageHandler,
      },
    },
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: true,
    },
    syntax: true,
    clipboard: {
      matchVisual: false,
    },
  };

  return (
    <Box>
      <RootStyle
        sx={{
          ...(error && {
            border: (theme) => `1px solid ${theme.palette.error.main}`,
          }),
          ...sx,
        }}>
        <EditorToolbar id={id} isSimple={simple} />

        <ReactQuill
          ref={quillRef}
          id={id}
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
          theme="snow"
          placeholder={
            placeholder ? t(placeholder) : t("global.description_place_holder")
          }
          {...other}
        />
      </RootStyle>

      {helperText && <Box sx={{ mt: 1 }}>{helperText}</Box>}
    </Box>
  );
}
